import { View } from "@common/types/views/views";
import { ViewConfig } from "@lib/views";
import type { RowData } from "@tanstack/react-table";

/**
 * Keeps a grouped view's expand column leftmost.
 *
 * That column hosts every group header, so moving it out of first position
 * strands each header mid-row with dead space to its left. Two separate
 * mechanisms can move it, so both need closing off:
 *
 * - ColumnSettings' drag reorders `view.columns`, so the column goes to index 0.
 * - TanStack hoists `columnPinning.left` columns ahead of unpinned ones, so
 *   pinning anything else left would overtake it. The column therefore joins the
 *   left bucket too, and index 0 puts it at that bucket's front (the bucket is
 *   ordered by `view.columns` — see buildColumns' `left` array).
 *
 * Same approach as the synthetic `select` column, and as `balanceSheetViewConfig`
 * pinning its label column. Users stay free to pin anything else; they just
 * can't get ahead of the group column. `immutable` on the config keeps the
 * resulting pin from being toggled off.
 */
export function withExpandColumnFirst<
    TData extends RowData,
    TColumn extends string,
    TSort extends string,
>({
    view,
    config,
}: {
    view: View;
    config: ViewConfig<TData, TColumn, TSort>;
}): View {
    const expandColumnId = config.nesting?.enabled
        ? config.nesting.expandColumn
        : undefined;

    if (expandColumnId === undefined) {
        return view;
    }

    const expandIndex = view.columns.findIndex(
        (column) => column.id === expandColumnId,
    );

    if (expandIndex === -1) {
        return view;
    }

    const expandColumn = view.columns[expandIndex];

    if (expandIndex === 0 && expandColumn.options?.pin === "left") {
        return view;
    }

    return {
        ...view,
        columns: [
            {
                ...expandColumn,
                options: { ...expandColumn.options, pin: "left" },
            },
            ...view.columns.filter((_, index) => index !== expandIndex),
        ],
    };
}
